import moment from "moment";

import { getPropertyAddress } from "./verifier";

/**
 * Owner portal payments grouping.
 *
 * A payment without a `paymentDate` has not been settled yet, so it is kept in a
 * dedicated "unpaid" bucket that is independent of the selected year. Settled
 * payments are grouped by the month of their `paymentDate`. Payments whose date
 * cannot be parsed land in `undated` so broken data stays visible instead of
 * disappearing from every year.
 */

export const MONTH_PAGE_SIZE = 25;

export function isUnpaid(payment) {
	return !payment?.paymentDate;
}

function timeValue(value) {
	if (!value) return 0;
	const parsed = moment(value);
	return parsed.isValid() ? parsed.valueOf() : 0;
}

function byIssuedDateDesc(a, b) {
	return timeValue(b?.issuedDate) - timeValue(a?.issuedDate);
}

function byPaymentDateDesc(a, b) {
	return timeValue(b?.paymentDate) - timeValue(a?.paymentDate);
}

export function groupPropertyPayments(property, year) {
	const unpaid = [];
	const undated = [];
	const buckets = new Map();
	const years = new Set();

	(property?.payments || []).forEach((payment) => {
		if (isUnpaid(payment)) {
			unpaid.push(payment);
			return;
		}

		const parsed = moment(payment.paymentDate);
		if (!parsed.isValid()) {
			undated.push(payment);
			return;
		}

		years.add(parsed.year());
		if (parsed.year() !== year) return;

		const monthIndex = parsed.month();
		if (!buckets.has(monthIndex)) buckets.set(monthIndex, []);
		buckets.get(monthIndex).push(payment);
	});

	const months = [...buckets.entries()]
		.sort(([a], [b]) => b - a)
		.map(([monthIndex, payments]) => ({ monthIndex, payments: payments.sort(byPaymentDateDesc) }));

	return {
		unpaid: unpaid.sort(byIssuedDateDesc),
		undated: undated.sort(byIssuedDateDesc),
		months,
		years,
	};
}

export function buildOwnerPaymentsView({ properties = [], year, query = "" } = {}) {
	const normalizedQuery = String(query || "")
		.trim()
		.toLocaleLowerCase("ro");
	const selectedYear = Number.isFinite(Number(year)) ? Number(year) : moment().year();
	const years = new Set([selectedYear, moment().year()]);

	const entries = (properties || []).map((property) => {
		const grouped = groupPropertyPayments(property, selectedYear);
		grouped.years.forEach((paymentYear) => years.add(paymentYear));

		return {
			propertyId: property?.id,
			address: getPropertyAddress(property),
			unpaid: grouped.unpaid,
			unpaidCount: grouped.unpaid.length,
			undated: grouped.undated,
			months: grouped.months,
			yearCount: grouped.months.reduce((total, month) => total + month.payments.length, 0),
		};
	});

	entries.sort((a, b) => String(a.address).localeCompare(String(b.address), "ro", { sensitivity: "base" }));

	const visible = normalizedQuery
		? entries.filter((entry) => String(entry.address).toLocaleLowerCase("ro").includes(normalizedQuery))
		: entries;

	return {
		year: selectedYear,
		years: [...years].sort((a, b) => b - a),
		properties: visible,
		propertyCount: entries.length,
		hiddenCount: entries.length - visible.length,
		unpaidCount: visible.reduce((total, entry) => total + entry.unpaidCount, 0),
	};
}
